import React, { useState } from 'react';
import { VenuePhoto } from '../../../types/venue';
import { theme } from '../../../theme';
import { X } from 'lucide-react';

interface VenuePhotoGalleryProps {
  photos: VenuePhoto[];
  venueName: string;
}

const VenuePhotoGallery: React.FC<VenuePhotoGalleryProps> = ({ photos, venueName }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!photos || photos.length === 0) {
    return (
      <div style={placeholderStyle}>
        <p style={{ color: theme.colors.textSecondary }}>Sin fotos disponibles</p>
      </div>
    );
  }

  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index);
  };

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <>
      <div style={containerStyle}>
        {/* Main Photo */}
        <div
          style={mainPhotoStyle}
          onClick={handleFullscreenToggle}
        >
          <img
            src={photos[selectedIndex].url}
            alt={photos[selectedIndex].caption || `${venueName} - Foto ${selectedIndex + 1}`}
            style={mainImageStyle}
          />
        </div>

        {/* Thumbnails Row */}
        {photos.length > 1 && (
          <div style={thumbRowStyle}>
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                style={{
                  ...thumbStyle,
                  border: index === selectedIndex
                    ? `2px solid ${theme.colors.primary}`
                    : '1px solid #33383B',
                }}
                onClick={() => handleThumbnailClick(index)}
              >
                <img
                  src={photo.url}
                  alt={photo.caption || `Thumbnail ${index + 1}`}
                  style={thumbImageStyle}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div style={fullscreenOverlayStyle} onClick={handleFullscreenToggle}>
          <img
            src={photos[selectedIndex].url}
            alt={photos[selectedIndex].caption || `${venueName} - Foto ${selectedIndex + 1}`}
            style={fullscreenImageStyle}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            style={closeButtonStyle}
            onClick={handleFullscreenToggle}
          >
            <X size={24} />
          </button>
        </div>
      )}
    </>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  width: '100%',
};

const placeholderStyle: React.CSSProperties = {
  width: '100%',
  height: 480,
  backgroundColor: '#23272A',
  borderRadius: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #33383B',
};

const mainPhotoStyle: React.CSSProperties = {
  width: '100%',
  height: 480,
  borderRadius: 18,
  overflow: 'hidden',
  cursor: 'pointer',
  border: '1px solid #33383B',
};

const mainImageStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const thumbRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  width: '100%',
};

const thumbStyle: React.CSSProperties = {
  flex: 1,
  height: 100,
  borderRadius: 12,
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'border 0.2s',
};

const thumbImageStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const fullscreenOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.95)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const fullscreenImageStyle: React.CSSProperties = {
  maxWidth: '90vw',
  maxHeight: '90vh',
  objectFit: 'contain',
};

const closeButtonStyle: React.CSSProperties = {
  position: 'absolute',
  top: 24,
  right: 24,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: '#1a1a1a',
  border: 'none',
  borderRadius: '50%',
  width: 48,
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

export default VenuePhotoGallery;
